import React from 'react';
import { deriveProgressState } from '../lib/deriveProgressState';

export type ReelProgressProps = {
  status: string;
  errorCode?: string | null;
  pollError?: string | null;
  onRetry?: () => void;
  className?: string;
};

/**
 * Studio-themed wait UI driven solely by deriveProgressState.
 */
const ReelProgress: React.FC<ReelProgressProps> = ({
  status,
  errorCode,
  pollError,
  onRetry,
  className,
}) => {
  const progress = deriveProgressState(status, errorCode);

  return (
    <div
      className={['w-full max-w-sm space-y-3', className].filter(Boolean).join(' ')}
      data-testid="rtts-reel-progress"
      data-stage={progress.stageKey}
    >
      <div className="flex items-baseline justify-between gap-3">
        <p className="text-sm font-medium text-white tracking-tight">{progress.copy}</p>
        {!progress.isError ? (
          <span className="text-[11px] uppercase tracking-wider text-secondary-500 shrink-0">
            Studio
          </span>
        ) : null}
      </div>

      {!progress.isError ? (
        <div
          className="h-1.5 overflow-hidden rounded-full bg-secondary-800"
          role="progressbar"
          aria-valuenow={progress.barPct}
          aria-valuemin={0}
          aria-valuemax={100}
          data-testid="rtts-progress-bar"
        >
          <div
            className={[
              'h-full rounded-full bg-gradient-to-r from-primary-600 via-primary-400 to-amber-300 transition-[width] duration-700 ease-out',
              progress.stageKey === 'detecting' || progress.stageKey === 'unknown'
                ? 'animate-pulse'
                : '',
            ]
              .filter(Boolean)
              .join(' ')}
            style={{ width: `${Math.max(progress.barPct, 6)}%` }}
          />
        </div>
      ) : null}

      {progress.isError || pollError ? (
        <div className="space-y-2" data-testid="rtts-progress-error">
          {pollError ? (
            <p className="text-xs text-red-300">{pollError}</p>
          ) : (
            <p className="text-xs text-red-300">{progress.copy}</p>
          )}
          {onRetry ? (
            <button
              type="button"
              className="btn-secondary text-xs"
              data-testid="rtts-progress-retry"
              onClick={onRetry}
            >
              Try again
            </button>
          ) : null}
        </div>
      ) : (
        <p className="text-[11px] text-secondary-500">
          Hang tight — this usually takes a few minutes.
        </p>
      )}
    </div>
  );
};

export default ReelProgress;
